/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { preload } from 'react-dom';

// React Router
import {
    type DataStrategyResult,
    isRouteErrorResponse,
    Links,
    Meta,
    type MetaDescriptor,
    type MiddlewareFunction,
    Navigate,
    Outlet,
    Scripts,
    ScrollRestoration,
    useLocation,
    useRevalidator,
    useRouteLoaderData,
    useSearchParams,
} from 'react-router';
import type { Route } from './+types/root';
import { routes } from '@/route-paths';

// Third-party libraries
import { createInstance, type i18n } from 'i18next';
import { I18nextProvider, useTranslation, initReactI18next } from 'react-i18next';
import { PageDesignerProvider } from '@salesforce/storefront-next-runtime/design/react/core';
import { isDesignModeActive, isPreviewModeActive } from '@salesforce/storefront-next-runtime/design/mode';
import { dataStoreMiddlewareLazy, sitesMiddlewareLazy } from '@salesforce/storefront-next-runtime/data-store';
import {
    buildUrl,
    SiteProvider,
    siteContext,
    type Site,
    type Locale,
} from '@salesforce/storefront-next-runtime/site-context';

// Middlewares
import authMiddlewareServer, { getAuth as getAuthServer } from '@/middlewares/auth.server';
import { getPublicSessionData } from '@/middlewares/auth.utils';
import createBasketMiddleware, { basketResourceContext, getBasket, type BasketSnapshot } from '@/middlewares/basket.server';
import shopperContextMiddlewareServer from '@/middlewares/shopper-context.server';
import { userTypeHintMiddleware } from '@/middlewares/user-type-hint.server';
import legacyRoutesMiddlewareClient from '@/middlewares/legacy-routes.client';
import {
    performanceMetricsMiddlewareClient,
    performanceMetricsMiddlewareServer,
} from '@/middlewares/performance-metrics';
import { appConfigMiddlewareServer } from '@/middlewares/app-config.server';
import { appConfigMiddlewareClient } from '@/middlewares/app-config.client';
import { ConfigProvider, getConfig, clientAppConfigContext } from '@salesforce/storefront-next-runtime/config';
import type { ClientAppConfig } from '@/lib/app-config-client';
import { siteContextMiddleware } from '@/middlewares/site-context.server';
import { sitesConfigMiddleware } from '@/middlewares/sites-config.server';
import { i18nextMiddleware } from '@/middlewares/i18next.server';
// @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
import {
    selectedStoreMiddleware,
    selectedStoreContext,
} from '@/extensions/store-locator/middlewares/selected-store.server';
import type { SelectedStoreInfo } from '@/extensions/store-locator/stores/store-locator-store';
// @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR
import { correlationMiddleware } from '@/middlewares/correlation.server';
import { requestOriginMiddleware } from '@/middlewares/request-origin';
import { getAppOrigin } from '@/lib/origin';
import { loggingMiddleware } from '@/middlewares/logging.server';
import { pageDesignerResolutionMiddleware } from '@/middlewares/page-designer-content-resolution.server';
import { siteUrlConfigMiddleware } from '@/middlewares/site-url-config.server';
import { modeDetectionMiddlewareServer, modeDetectionMiddlewareClient } from '@/middlewares/mode-detection';
import { maintenanceMiddleware } from '@/middlewares/maintenance.server';
import { securityHeadersMiddleware } from '@/middlewares/security-headers.server';
import { stripScriptNonceMiddleware } from '@/middlewares/strip-script-nonce.server';
import { getSecurityNonce } from '@salesforce/storefront-next-runtime/security';
import { useSecurityNonceFromContext } from '@salesforce/storefront-next-runtime/security/react';
import { errorCacheControlMiddleware } from '@/middlewares/error-cache-control.server';

// Providers
import AuthProvider, { useAuth } from '@/providers/auth';
import BasketProvider from '@/providers/basket';
import { ComposeProviders } from '@/providers/compose-providers';
import { CorrelationProvider } from '@/providers/correlation';
import { PasskeyRegistrationProvider } from '@/providers/passkey-registration';
import { correlationContext } from '@/lib/correlation';
import { ModalProvider, useModalContext } from '@/contexts/modal-context';

// Components
import { AppToaster } from '@/components/toast';
import { TrackingConsentBanner } from '@/components/tracking-consent-banner';
import CimulateAgent, { isCimulateEnabled } from '@/components/cimulate';

// Hooks
import { useExecutePendingAction } from '@/hooks/use-execute-pending-action';
import { initGtm } from '@/lib/gtm';

// Lib/Utils
import type { PublicSessionData } from '@/lib/api/types';
import { useCurrency } from '@/lib/currency/use-currency';
import { getTranslation } from '@salesforce/storefront-next-runtime/i18n';
import { PageViewTracker } from '@/analytics/page-view-tracker';
import { initializeRegistry } from '@/lib/page-designer/static-registry';
import { buildSeoMetaDescriptors } from '@/utils/seo';

// Server-only
import { fetchPage } from '@/lib/api/page.server';
import type { Page } from '@/lib/util/pageLoader.server';
import { getCustomSitePreferences } from '@/lib/site-preferences.server';

// Assets
import favicon from '/favicon.png';

// Fonts
import { primaryFont } from '@/lib/fonts';

// Styles
import { PageDesignerInit } from '@/page-designer-init';
import appStylesHref from '@/theme/index.css?url';

// Extensions
import { UITargetProviders } from '@/targets/ui-target-providers';
// @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
import StoreLocatorProvider from '@/extensions/store-locator/providers/store-locator';
// @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR
import { type Maintenance, maintenanceContext } from '@/lib/maintenance';

// Layout Components - logo for error page. Imported via `@/...` so different
// logo implementations (raster, inline-SVG, etc.) can be provided per brand.
import Logo from '@/components/logo';
import { SkipLink } from '@/components/skip-link';

/** Extracts the first img src from the fj-member-access-pd PD content asset. */
function extractInsiderLogoUrl(page: Page | null): string | null {
    if (!page?.regions) return null;
    for (const region of page.regions) {
        for (const comp of region.components ?? []) {
            const data = comp.data as Record<string, unknown> | undefined;
            if (
                (comp.typeId === 'Content.markupContent' || comp.typeId === 'Content.contentAsset') &&
                typeof data?.markup === 'string'
            ) {
                const match = (data.markup as string).match(/<img[^>]+src=["']([^"']+)["']/i);
                if (match?.[1]) return match[1];
            }
        }
    }
    return null;
}

export const links: Route.LinksFunction = () => {
    return [
        // Preload critical fonts
        { rel: 'preload', href: primaryFont, as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' },
        { rel: 'preload', href: appStylesHref, as: 'style' },
        { rel: 'stylesheet', href: appStylesHref },
    ];
};

export const meta: Route.MetaFunction = ({ loaderData }) => {
    return loaderData?.seoMeta ?? [];
};

export const middleware: MiddlewareFunction<Response>[] = [
    correlationMiddleware,
    errorCacheControlMiddleware,
    // Must be before securityHeadersMiddleware — React Router 7 uses an onion model so
    // earlier middlewares post-process the response after later ones have run, giving this
    // middleware access to the fully-assembled CSP header to strip the nonce from script-src.
    stripScriptNonceMiddleware,
    requestOriginMiddleware,
    loggingMiddleware,
    modeDetectionMiddlewareServer,
    appConfigMiddlewareServer,
    securityHeadersMiddleware,
    // Registers the lazy DAL sites loader, then (behind commerce.sitesFromDal) rewrites
    // commerce.sites before siteContextMiddleware reads it. Both must run after appConfig
    // populates the config contexts and before siteContextMiddleware resolves the site.
    sitesMiddlewareLazy,
    sitesConfigMiddleware,
    siteContextMiddleware, // Must run after appConfig, before i18next and currency
    ...dataStoreMiddlewareLazy,
    siteUrlConfigMiddleware, // Must run after siteContextMiddleware (entry key uses site id)
    i18nextMiddleware,
    pageDesignerResolutionMiddleware,
    selectedStoreMiddleware /** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */,
    performanceMetricsMiddlewareServer,
    maintenanceMiddleware,
    authMiddlewareServer,
    createBasketMiddleware(),
    shopperContextMiddlewareServer,
    // Must run after authMiddlewareServer (reads the final session userType). Writes the JS-readable
    // `__sfdc_usertype` hint in its response phase so AuthProvider can restore the header auth state
    // under a cached app shell. Writes only the userType hint — never the httpOnly token cookies.
    userTypeHintMiddleware,
];

export const clientMiddleware: MiddlewareFunction<Record<string, DataStrategyResult>>[] = [
    // Client middleware functions have varying return types, but React Router expects Record<string, DataStrategyResult>
    // We cast through unknown to avoid type errors while maintaining runtime correctness
    appConfigMiddlewareClient as unknown as MiddlewareFunction<Record<string, DataStrategyResult>>, // Must run first to set config in context
    modeDetectionMiddlewareClient,
    legacyRoutesMiddlewareClient as unknown as MiddlewareFunction<Record<string, DataStrategyResult>>, // Checks hybrid.enabled, needs config from context
    performanceMetricsMiddlewareClient as unknown as MiddlewareFunction<Record<string, DataStrategyResult>>,
];

// Client-side i18next instance. Hydration is deferred until its initial-language
// namespaces have loaded — see `entry.client.tsx` / `whenI18nReady()` — so the
// first client render matches the server-rendered translated HTML (no hydration
// mismatch) without inlining the translation bundle into the document.
import { i18nextOnClient } from '@/i18n-client-init';

export { shouldRevalidate } from '@/lib/revalidation/routes/root';

export const loader = async ({
    context,
    request,
}: Route.LoaderArgs): Promise<{
    // Public auth data - only non-sensitive fields, safe to serialize
    clientAuth: PublicSessionData;
    // Client-safe view: extractClientConfig strips app.serverExtension before this loader
    // returns, since React Router serializes the loader return into the SSR hydration
    // payload and reaches the browser. Server-only reads of those values still go through
    // getConfig(context).serverExtension, which reads from appConfigContext (set by
    // appConfigMiddlewareServer with the full AppConfig), not the loader return.
    appConfig: ClientAppConfig;
    basketSnapshot: BasketSnapshot | null;
    maintenance: Maintenance;
    locale: Locale;
    site: Site;
    currency: string;
    selectedStoreInfo: SelectedStoreInfo | null /** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */;
    correlationId: string;
    pageDesignerMode: 'EDIT' | 'PREVIEW' | undefined;
    // Pre-computed in the loader (server-only) so seo.ts stays out of the client bundle
    seoMeta: MetaDescriptor[];
    // Return as function to prevent i18next instance serialization
    getI18next: () => i18n;
    // Serialized error namespace for the active locale — used by ErrorBoundary on SSR/hydration
    errorTranslations: Record<string, unknown>;
    // CSP nonce for inline scripts; null when security headers middleware is disabled
    nonce: string | null;
    // Image URL for the FJ Insider logo, resolved server-side when utm_content opens the modal on load
    memberAccessLogoUrl: string | null;
    // UsableNet accessibility script URL from site preferences; null when not configured
    usableNetScriptUrl: string | null;
}> => {
    const session = getAuthServer(context);

    const appConfig = getConfig(context);

    // On the server side, our middleware stores the translations in this i18next object
    // so we'll need to be careful not to accidentally serialize this object (to avoid bloating the html).
    const { i18next } = getTranslation(context);

    // @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
    const selectedStoreInfo = context.get(selectedStoreContext) ?? null;
    // @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR

    // Get resolved site, locale, and currency from site context middleware
    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        throw new Error('Site context not found. Ensure siteContextMiddleware runs before loaders.');
    }
    const { locale, site, currency } = siteCtx;

    // When returning from an external page (SFCC Classic cart), the __sfdc_basket cookie may be stale.
    // If the pagehide handler in useRefreshBasketOnReturn set sfdc_basket_needs_check=1, eagerly fetch
    // the basket here so the fresh count is in both the SSR HTML and the Set-Cookie response header
    // before React hydrates — eliminating the old-count flash on return.
    // When there is no existing basket ID, use ensureBasket:true so that getOrCreateBasket(null) is
    // called. SCAPI's createBasket returns the session's existing basket (via dwsid) rather than
    // creating a new one, allowing SFN to discover a basket created by classic SFCC (e.g. MyJoys).
    const cookieHeader = request.headers.get('cookie') ?? '';
    if (cookieHeader.includes('sfdc_basket_needs_check=1')) {
        const existingSnapshot = context.get(basketResourceContext)?.snapshot;
        const hasExistingBasket = Boolean(existingSnapshot?.basketId);
        await getBasket(context, { ensureBasket: hasExistingBasket ? 'read' : true }).catch(() => {});
    }

    // Load the application basket provider with the basket snapshot. We are actively not loading the basket, as
    // we want to lazy load the basket when the basket is needed. This prevents low-engagement users from causing
    // unnecessary resource usage in the form of basket creations.
    const basketSnapshot = context.get(basketResourceContext)?.snapshot ?? null;

    // Get correlation ID from middleware for request tracing
    const correlationId = context.get(correlationContext);

    // Get maintenance data from middleware
    const maintenance = context.get(maintenanceContext);

    // Extract only non-sensitive fields for client - tokens stay server-side only
    const clientAuth = getPublicSessionData(session);

    const requestUrl = new URL(request.url);

    // When the insider modal opens on page load (utm_content=login/register), kick off the
    // PD page fetch immediately — in parallel with the rest of the loader — so the logo URL
    // is available server-side and can be preloaded in the HTML <head> before JS runs.
    const utmContent = requestUrl.searchParams.get('utm_content');
    const memberAccessPdPromise =
        utmContent === 'login' || utmContent === 'register'
            ? fetchPage(context, { pageId: 'fj-member-access-pd' }).catch(() => null)
            : null;

    const seoMeta = buildSeoMetaDescriptors({
        site,
        appConfig,
        // Use the resolved public origin (custom domain on MRT) rather than
        // requestUrl.origin (lambda-internal hostname on hybrid deployments)
        // so canonical/hreflang URLs match what the customer is actually browsing.
        origin: getAppOrigin(context),
        locale,
        location: { pathname: requestUrl.pathname, search: requestUrl.search },
    });

    // CSP nonce produced by securityHeadersMiddleware; null when middleware is disabled.
    const nonce = getSecurityNonce(context);

    const sitePrefs = await getCustomSitePreferences(context);

    const memberAccessLogoUrl = memberAccessPdPromise
        ? extractInsiderLogoUrl(await memberAccessPdPromise)
        : null;

    return {
        // Read the precomputed client view from the context — `appConfigMiddlewareServer`
        // strips server-only namespaces once at module init and stashes the result, so this
        // is a zero-cost lookup, not a per-request allocation. The loader return is
        // serialized by React Router into the SSR hydration payload and reaches the browser
        // unconditionally; server-side reads of the stripped namespaces stay available
        // through getConfig(context), which reads the unstripped appConfigContext.
        appConfig: context.get(clientAppConfigContext) as ClientAppConfig,
        basketSnapshot,
        locale,
        site,
        currency,
        selectedStoreInfo /** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */,
        correlationId,
        maintenance,
        clientAuth,
        seoMeta,
        getI18next: () => i18next,
        errorTranslations: (i18next.getResourceBundle(i18next.language, 'routeError') as Record<string, unknown>) ?? {},
        pageDesignerMode: isDesignModeActive(request) ? 'EDIT' : isPreviewModeActive(request) ? 'PREVIEW' : undefined,
        nonce,
        memberAccessLogoUrl,
        usableNetScriptUrl: sitePrefs.usableNetScriptUrl ?? null,
    };
};

// This creates a union type where properties unique to either loader are optional
// Properties present in both loaders remain required
type ServerLoaderData = Awaited<ReturnType<typeof loader>>;
type LoaderData = ServerLoaderData;

export function Layout({ children }: PropsWithChildren) {
    const data = useRouteLoaderData<LoaderData>('root');
    // The root loader already strips server-only namespaces (app.serverExtension) from
    // `appConfig` before returning it, so window.__APP_CONFIG__ inherits the same clean
    // shape \u2014 the loader return is the single source of truth for the client view, since
    // React Router serializes that return into the SSR hydration payload regardless of
    // what the inline window.__APP_CONFIG__ script contains.
    const appConfig = data?.appConfig;
    const gtmContainerId = appConfig?.gtm?.containerId;
    const gtmEnabled = appConfig?.gtm?.enabled !== false && Boolean(gtmContainerId);
    const appConfigScript = appConfig
        ? `window.__APP_CONFIG__ = ${JSON.stringify(appConfig)
              .replace(/</g, '\\u003c')
              .replace(/>/g, '\\u003e')
              .replace(/&/g, '\\u0026')
              .replace(/\u2028/g, '\\u2028')
              .replace(/\u2029/g, '\\u2029')};`
        : '';

    // React 19 omits the attribute when the value is undefined, so coerce null/missing to undefined.
    // Falls back to the NonceContext so the error path (root loader threw → no
    // loader data) still gets a valid nonce on its inline scripts. The middleware
    // sets `securityContext` *before* `next()`, and `entry.server.tsx` wraps
    // the SSR tree with <NonceContext.Provider>, so the context is populated
    // even when the loader fails.
    const nonceFromContext = useSecurityNonceFromContext();
    const nonce = data?.nonce ?? nonceFromContext ?? undefined;

    const i18next = typeof window === 'undefined' ? data?.getI18next?.() : i18nextOnClient;
    const lang = i18next?.language ?? 'en';
    const dir = i18next?.dir(lang) ?? 'ltr';

    const usableNetScriptUrl = data?.usableNetScriptUrl ?? null;

    // Inject UsableNet after React hydration to prevent DOM reconciliation conflicts.
    // UsableNet injects overlay HTML directly into the DOM; a static <script> tag (even
    // at the bottom of <body>) can run while React is still hydrating, causing error #418.
    useEffect(() => {
        if (!usableNetScriptUrl) return;
        if (document.getElementById('usntA42start')) return;
        const script = document.createElement('script');
        script.id = 'usntA42start';
        script.src = usableNetScriptUrl;
        script.dataset.rapid = 'true';
        script.async = true;
        document.body.appendChild(script);
    }, [usableNetScriptUrl]);

    return (
        <html lang={lang} dir={dir}>
            <head>
                <meta charSet="utf-8" />
                <link rel="icon" type="image/png" href={favicon} />
                {appConfig?.links?.preconnect?.map((origin: string) => (
                    <link key={origin} rel="preconnect" href={origin} />
                ))}
                {appConfig?.links?.prefetchDns?.map((origin: string) => (
                    <link key={origin} rel="dns-prefetch" href={origin} />
                ))}
                {appConfig?.links?.prefetch?.map((href: string) => (
                    <link key={href} rel="prefetch" href={href} />
                ))}
                <link rel="stylesheet" href="https://use.typekit.net/ymc0zaq.css" />
                <script
                    nonce={nonce}
                    // Browsers strip the `nonce` content attribute from the DOM after
                    // applying CSP (so scripts can't read each other's nonce), leaving
                    // `nonce=""` in the client DOM while the server rendered the real
                    // value. On this hand-written inline script that surfaces as a
                    // hydration attribute mismatch. React's own <Scripts>/<ScrollRestoration>
                    // handle this internally; here we suppress the warning for the same
                    // browser behavior. The CSP nonce itself still applies correctly.
                    //
                    // Note: this also suppresses any server/client divergence of the
                    // __APP_CONFIG__ body, not just the nonce attribute. Safe today
                    // because appConfig is server-rendered and never mutated on the
                    // client. If client-side config mutation is ever introduced, revisit
                    // this — a real config mismatch would be silently hidden here.
                    suppressHydrationWarning
                    dangerouslySetInnerHTML={{
                        __html: `
                        ${appConfigScript}
                    `,
                    }}
                />
                {gtmEnabled && gtmContainerId && (
                    <script
                        nonce={nonce}
                        suppressHydrationWarning
                        dangerouslySetInnerHTML={{
                            // Google Consent Mode v2 defaults — MUST run before the GTM IIFE.
                            //
                            // Two rules:
                            //
                            // 1. Use a plain Array push instead of window.gtag('consent',…).
                            //    Calling window.gtag() pushes an Arguments object which has
                            //    special properties (callee, Symbol.iterator) that gtag/js's
                            //    recursive Gb traversal follows into unexpected paths, causing
                            //    "too much recursion". A plain array ['consent','default',{…}]
                            //    has none of those properties.
                            //
                            // 2. Set wait_for_update to 0 (no queuing window).
                            //    A non-zero value causes gtag to queue every dataLayer push until
                            //    Usercentrics signals consent. When consent arrives, gtag replays
                            //    the queue via Gb recursively. After many navigations the queue
                            //    is large enough to overflow the call stack. With 0, events are
                            //    processed immediately with the default denied state; Usercentrics
                            //    calls gtag('consent','update',…) when the user acts.
                            // Initialize dataLayer and dataLayerData here (previously done in the
                            // GTM IIFE, which is now removed — GTM loads client-side only via
                            // initGtm() in the App useEffect to avoid racing with React effects).
                            __html: `window.dataLayer=window.dataLayer||[];window.dataLayerData=window.dataLayerData||{};window.dataLayer.push(['consent','default',{'ad_storage':'denied','analytics_storage':'denied','ad_user_data':'denied','ad_personalization':'denied','wait_for_update':0}]);`,
                        }}
                    />
                )}
                {/* GTM IIFE intentionally removed — GTM loads client-side only via initGtm() in
                    the App useEffect. Loading it server-side causes gtm.js to start before React
                    effects fire, creating a race: PLP's view_item_list push (22+ products) lands
                    in the native dataLayer queue before gtag installs its handler. When gtag batch-
                    processes the full queue, Gb's recursive traversal overflows the call stack.
                    Client-side loading ensures GTM only starts after the App useEffect (which fires
                    after all child effects), so the preGtagEcommerceQueue buffer catches ecommerce
                    events before gtag loads and flushes them one-at-a-time after. */}
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <Meta />
                <Links />
            </head>
            <body className="antialiased flex flex-col min-h-screen">
                {gtmEnabled && gtmContainerId && (
                    <noscript>
                        <iframe
                            src={`https://www.googletagmanager.com/ns.html?id=${gtmContainerId}`}
                            height="0"
                            width="0"
                            style={{ display: 'none', visibility: 'hidden' }}
                        />
                    </noscript>
                )}
                {children}
                <AppToaster />
                {/* nonce intentionally omitted: stripScriptNonceMiddleware removes the
                    nonce from script-src, making 'unsafe-inline' effective. Passing nonce
                    here causes React Router to stamp it on every code-split chunk script
                    it creates at runtime, which triggers infinite recursion in gtag/js. */}
                <ScrollRestoration />
                <Scripts />
            </body>
        </html>
    );
}

/**
 * Error page content component with i18n support.
 * When called without an I18nextProvider (early error before loader ran), t falls back to
 * the key string — callers must pass translated strings directly via props in that case.
 */
function ErrorPageContent({
    status,
    details,
    stack,
    homepageUrl,
    title,
    message,
    secondaryMessage,
    statusDetails,
    goToHomepageLabel,
    allRightsReservedLabel,
}: {
    status: number | undefined;
    details: string | undefined;
    stack: string | undefined;
    homepageUrl: string;
    title: string;
    message?: string;
    secondaryMessage?: string;
    statusDetails?: string;
    goToHomepageLabel: string;
    allRightsReservedLabel: string;
}) {
    return (
        <>
            <SkipLink />
            {/* Simple Header */}
            <header className="bg-header-background text-header-foreground sticky top-0 z-50">
                <div className="section-container">
                    <div className="flex items-center gap-x-4 lg:gap-x-6 h-16">
                        <a href={homepageUrl} className="flex-shrink-0 flex items-center">
                            <Logo className="h-3 lg:h-4 w-auto [filter:var(--header-logo-filter)]" />
                        </a>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main id="main-content" tabIndex={-1} className="grow pt-8">
                <div className="flex items-center justify-center min-h-[60vh] px-4 py-12">
                    <div className="mx-auto max-w-3xl w-full text-center">
                        {/* Large status code */}
                        {status && <div className="text-error-status font-bold leading-none mb-8">{status}</div>}

                        <h1 className="text-4xl md:text-5xl font-bold mb-6">{title}</h1>

                        {message && <p className="text-lg text-muted-foreground mb-4 max-w-2xl mx-auto">{message}</p>}
                        {secondaryMessage && (
                            <p className="text-base text-muted-foreground mb-4 max-w-2xl mx-auto">{secondaryMessage}</p>
                        )}
                        {statusDetails && (
                            <p className="text-sm text-muted-foreground mb-12 max-w-2xl mx-auto opacity-50">
                                {statusDetails}
                            </p>
                        )}

                        {/* For non-route errors: show technical details */}
                        {!status && details && (
                            <p className="text-lg text-muted-foreground mb-12 max-w-2xl mx-auto">Error: {details}</p>
                        )}

                        {/* Back to home button */}
                        <div>
                            <a
                                href={homepageUrl}
                                className="inline-block bg-primary px-12 py-3 text-base font-semibold text-primary-foreground no-underline transition-colors hover:bg-primary/90">
                                {goToHomepageLabel}
                            </a>
                        </div>

                        {/* Stack trace (only in dev mode with stack) */}
                        {stack && (
                            <div className="mt-16 border border-border bg-muted/30 text-left">
                                <div className="flex items-center px-4 py-3 border-b border-border">
                                    <h2 className="text-sm font-semibold text-foreground">Stack Trace</h2>
                                </div>
                                <pre className="p-4 overflow-auto max-h-80 text-xs leading-relaxed text-foreground/90 font-mono">
                                    <code>{stack}</code>
                                </pre>
                                <div className="px-4 py-3 border-t border-border">
                                    <p className="text-xs text-muted-foreground">
                                        To disable stack traces in production, turn off{' '}
                                        <code className="text-xs">unstable_devTools</code> in your router config.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* Simple Footer */}
            <footer className="mt-auto bg-background border-t border-border">
                <div className="mx-auto max-w-7xl section-container py-8">
                    <p className="text-center text-sm text-muted-foreground">
                        © {new Date().getFullYear()} {allRightsReservedLabel}
                    </p>
                </div>
            </footer>
        </>
    );
}

/**
 * Renders the error page using translations from the root loader payload.
 * Requires an I18nextProvider wrapping it.
 */
function TranslatedErrorPageContent({
    status,
    details,
    stack,
    homepageUrl,
}: {
    status: number | undefined;
    details: string | undefined;
    stack: string | undefined;
    homepageUrl: string;
}) {
    const { t } = useTranslation('routeError');

    let title: string;
    let message: string | undefined;
    let secondaryMessage: string | undefined;
    let statusDetails: string | undefined;

    if (status === 404) {
        title = t('404.title');
        message = t('404.message');
        secondaryMessage = t('404.secondaryMessage');
        statusDetails = t('404.details');
    } else if (status === 403) {
        title = t('403.title');
        message = t('403.message');
        secondaryMessage = t('403.secondaryMessage');
    } else if (status === 500) {
        title = t('500.title');
        message = t('500.message');
        secondaryMessage = t('500.secondaryMessage');
    } else {
        title = t('defaultTitle');
    }

    return (
        <ErrorPageContent
            status={status}
            details={details}
            stack={stack}
            homepageUrl={homepageUrl}
            title={title}
            message={message}
            secondaryMessage={secondaryMessage}
            statusDetails={statusDetails}
            goToHomepageLabel={t('goToHomepage')}
            allRightsReservedLabel={t('allRightsReserved')}
        />
    );
}

export function ErrorBoundary({ error }: { error: unknown }) {
    // All hooks must be called unconditionally before any early returns.
    const rootData = useRouteLoaderData<typeof loader>('root');

    const language =
        (typeof window !== 'undefined' && i18nextOnClient ? i18nextOnClient.language : undefined) ||
        (typeof document !== 'undefined' ? document.documentElement.lang : undefined) ||
        rootData?.locale?.id ||
        'en';

    const errorTranslations = rootData?.errorTranslations;
    const fallbackLng = rootData?.appConfig.i18n.fallbackLng ?? 'en';

    // Only created when errorTranslations is available (root loader ran).
    // When the loader did not run (e.g. middleware crash), errorTranslations is undefined and we
    // skip i18next entirely — rendering hardcoded English strings directly avoids any static import
    // of locale JSON that would cause Vite to bundle/preload locale chunks on every page.
    const i18nextInstance = useMemo(() => {
        if (!errorTranslations || Object.keys(errorTranslations).length === 0) return null;
        const resources: Record<string, { routeError: Record<string, unknown> }> = {
            [fallbackLng]: { routeError: errorTranslations },
            [language]: { routeError: errorTranslations },
        };
        const instance = createInstance();
        void instance.use(initReactI18next).init({
            lng: language,
            fallbackLng,
            resources,
            interpolation: { escapeValue: false },
            initAsync: false,
        });
        return instance;
    }, [language, errorTranslations, fallbackLng]);

    const homepageUrl = rootData?.site
        ? buildUrl({
              to: '/',
              urlConfig: rootData.appConfig.url,
              params: {
                  siteId: rootData.site.alias ?? rootData.site.id,
                  localeId: rootData.appConfig.localeAliasMap?.[rootData.locale.id] ?? rootData.locale.id,
              },
          })
        : '/';

    // Redirect maintenance errors before rendering.
    if (error && error.toString().includes('MAINTENANCE_ERROR')) {
        return <Navigate to={routes.maintenance} replace />;
    }

    let status: number | undefined;
    let details: string | undefined;
    let stack: string | undefined;

    if (isRouteErrorResponse(error)) {
        status = error.status;
        details = error.statusText;
    } else if (error instanceof Error) {
        details = error.message;
        stack = error.stack;
    } else if (typeof error === 'string') {
        details = error;
    }

    // Root loader ran — render with active locale translations.
    if (i18nextInstance) {
        return (
            <I18nextProvider i18n={i18nextInstance}>
                <TranslatedErrorPageContent status={status} details={details} stack={stack} homepageUrl={homepageUrl} />
            </I18nextProvider>
        );
    }

    // Root loader did not run (e.g. middleware crash before loader executed).
    // No locale context available — render hardcoded English strings with no i18next dependency.
    const englishContent = {
        404: { title: 'Page not found', message: 'The requested page could not be found.' },
        403: { title: 'Access restricted', message: "You don't have permission to view this page." },
        500: { title: 'Something went wrong', message: 'An unexpected error occurred. Please try again later.' },
    };
    const fallbackContent =
        status && status in englishContent
            ? englishContent[status as keyof typeof englishContent]
            : { title: 'Something went wrong', message: undefined };

    return (
        <ErrorPageContent
            status={status}
            details={details}
            stack={stack}
            homepageUrl={homepageUrl}
            title={fallbackContent.title}
            message={fallbackContent.message}
            goToHomepageLabel="Go to Homepage"
            allRightsReservedLabel="All rights reserved."
        />
    );
}

export default function App({
    loaderData: {
        clientAuth,
        basketSnapshot,
        getI18next,
        currency: loaderCurrency,
        correlationId,
        pageDesignerMode,
        site,
        locale,
        // @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
        selectedStoreInfo,
        // @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR
        memberAccessLogoUrl,
    },
}: {
    loaderData: LoaderData;
}) {
    // Preload the FJ Insider logo when the modal will open on page load.
    // Called during render so React 19 emits <link rel="preload"> in the SSR HTML <head>
    // before any JS runs — the browser starts the image download at the earliest possible moment.
    if (memberAccessLogoUrl) {
        preload(memberAccessLogoUrl, { as: 'image', fetchPriority: 'high' });
    }

    // Currency is always provided by loader (which reads from middleware)
    if (!loaderCurrency) {
        throw new Error('Currency is required but not provided by loader');
    }

    // Get app configuration from server loader data (initial load) or window.__APP_CONFIG__ (client nav)
    // This ensures config is read from MRT environment variables (via middleware), not baked at build time
    const serverData = useRouteLoaderData('root');
    const appConfig = serverData?.appConfig || (typeof window !== 'undefined' ? window.__APP_CONFIG__ : undefined);
    if (!appConfig) {
        throw new Error('App configuration not available - check server loader and window.__APP_CONFIG__');
    }

    // In server-only auth architecture:
    // - clientAuth contains only non-sensitive fields (userType, customerId, usid, etc.)
    // - These values are serialized directly from the server loader
    // - No client middleware or bootstrap needed - server is the single source of truth
    // - Tokens (accessToken, refreshToken) stay server-side only

    // Initialize Page Designer components
    initializeRegistry();

    const i18next = (typeof window === 'undefined' ? getI18next?.() : i18nextOnClient) as i18n;

    // Under a cached shell the loader currency is frozen; restore from the cookie post-hydration.
    // The cookie name is bundled app config (never changes at runtime), so read it straight from
    // appConfig rather than threading it through the loader.
    const currency = useCurrency(
        loaderCurrency,
        site.supportedCurrencies,
        appConfig.siteContext?.currencyCookieName ?? 'currency'
    );

    // Memoize the providers array to prevent unnecessary remounting of providers on render
    const providers = useMemo(
        () =>
            [
                [I18nextProvider, { i18n: i18next }],
                [ConfigProvider, { config: appConfig }],
                // Site provider will contain info about site/locale/currency on single request.
                // include i18next.language since these infos tend to go together.
                // site will drive the language/locale and currency
                [SiteProvider, { site, locale, language: i18next.language, currency }],
                // siteId resolves the namespaced `__sfdc_usertype` hint cookie so AuthProvider can
                // restore the header auth state under a cached app shell. Must be the raw site.id
                // (not the alias) to match the cookie the userType-hint middleware writes.
                [AuthProvider, { value: clientAuth, siteId: site.id }],
                [BasketProvider, { snapshot: basketSnapshot }],
                [CorrelationProvider, { value: correlationId }],
                // @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
                [StoreLocatorProvider, { selectedStoreInfo }],
                // @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR
            ] as const,
        [
            correlationId,
            i18next,
            appConfig,
            currency,
            clientAuth,
            basketSnapshot,
            site,
            locale,
            // @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
            selectedStoreInfo,
            // @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR
        ]
    );

    const hybridEnabled = Boolean(appConfig?.hybrid?.enabled);

    // Initialise GTM once on the client side using react-gtm-module-nonce-aware.
    // The inline script in Layout already loads the GTM library on first render;
    // this call registers the dataLayer and keeps react-gtm-module in sync.
    useEffect(() => {
        const gtmId = appConfig?.gtm?.containerId;
        if (appConfig?.gtm?.enabled !== false && gtmId) {
            const gtmNonce = (serverData as Record<string, unknown> | null)?.nonce as string | undefined;
            initGtm(gtmId, gtmNonce);
        }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const passkeyEnabled = Boolean(appConfig?.features?.passkey?.enabled);

    const innerTree = (
        <UITargetProviders>
            <AuthActionExecutor />
            {passkeyEnabled && <PasskeyRegistrationTrigger />}
            {hybridEnabled && <BackNavigationRevalidator />}
            <PageDesignerProvider
                clientId="storefront-next"
                targetOrigin="*"
                usid={clientAuth?.usid}
                mode={pageDesignerMode}>
                <PageDesignerInit />
                <Outlet />
            </PageDesignerProvider>
            <TrackingConsentBanner />
            {typeof window !== 'undefined' && <PageViewTracker />}
        </UITargetProviders>
    );

    return (
        <ComposeProviders providers={providers}>
            <ModalProvider>
                <UtmModalAutoOpen />
                {passkeyEnabled ? <PasskeyRegistrationProvider>{innerTree}</PasskeyRegistrationProvider> : innerTree}
                {isCimulateEnabled(appConfig.cimulateAgent?.enabled) && (
                    <CimulateAgent cimulateConfiguration={appConfig.cimulateAgent} />
                )}
            </ModalProvider>
        </ComposeProviders>
    );
}

/**
 * Opens a marketing overlay on page load based on utm_content. Runs only once per mount.
 * - utm_content=login|register opens the FJ Insider modal; skips for authenticated users,
 *   and the modal receives the clean URL (utm_content stripped) as its returnUrl.
 * - utm_content=mobileoptin opens the SMS signup modal, regardless of auth state.
 */
function UtmModalAutoOpen() {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { openInsiderModal, openSmsModal } = useModalContext();
    const session = useAuth();
    const openedRef = useRef(false);

    useEffect(() => {
        if (openedRef.current) return;

        const utmContent = searchParams.get('utm_content');

        if (utmContent === 'mobileoptin') {
            openedRef.current = true;
            openSmsModal();
            return;
        }

        const isAuthenticated = session?.userType === 'registered' && !!session?.customerId;
        if (isAuthenticated) return;

        if (utmContent !== 'login' && utmContent !== 'register') return;

        openedRef.current = true;
        const cleanParams = new URLSearchParams(searchParams);
        cleanParams.delete('utm_content');
        const cleanSearch = cleanParams.toString();
        const cleanUrl = location.pathname + (cleanSearch ? `?${cleanSearch}` : '');
        openInsiderModal(utmContent as 'login' | 'register', cleanUrl);
    }, [session, searchParams, location.pathname, openInsiderModal, openSmsModal]);

    return null;
}

/**
 * Component that executes pending actions after authentication
 * This runs on every route to check if there are actions queued from auth interception
 */
function AuthActionExecutor() {
    useExecutePendingAction();
    return null;
}

function PasskeyRegistrationTrigger() {
    usePasskeyRegistration();
    return null;
}

/**
 * Revalidates loader data once on back/forward (e.g. back from SFRA). Mounted only when
 * hybrid is enabled; ensures UI is fresh after a full-page redirect. No-op when hybrid is off.
 */
function BackNavigationRevalidator() {
    const revalidator = useRevalidator();
    const didRevalidateRef = useRef(false);
    useEffect(() => {
        if (didRevalidateRef.current) return;
        const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        if (nav?.type === 'back_forward' && revalidator.state === 'idle') {
            didRevalidateRef.current = true;
            void revalidator.revalidate();
        }
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
}
